import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AppLayout from '../../components/layout/AppLayout'
import MobilePurchaseReport from '../../components/purchase/mobile/MobilePurchaseReport'
import { useIsMobile } from '../../hooks/useIsMobile'
import { useReplenishment } from '../../hooks/useReplenishment'
import { emitPurchaseOrder } from '../../hooks/usePurchaseOrders'
import { supabase } from '../../lib/supabase'
import { useToast } from '../../hooks/useToast'
import type { Company, ProductRow, Supplier } from '../../types'

interface SelectableRow extends ProductRow {
  qty: number
  selected: boolean
}

export default function PurchaseReportPage() {
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const { rows, loading } = useReplenishment()
  const [overrides, setOverrides] = useState<Record<string, { qty: number; selected: boolean }>>({})
  const [emitting, setEmitting] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const { notify } = useToast()

  const { groups, orphanCount } = useMemo(() => {
    // Regra: itens "Compra Aprovada" (gera Pedido de Compra) ou "Consignado" (gera Pedido de Consignação)
    // ainda sem pedido aberto.
    const candidates = rows.filter(r =>
      (r.kanban_status === 'Compra Aprovada' || r.kanban_status === 'Consignado') &&
      !r.active_order_number &&
      r.approved_qty > 0
    )
    const map = new Map<string, { supplier: Supplier; rows: SelectableRow[] }>()
    let orphans = 0
    candidates.forEach(r => {
      if (!r.suppliers) { orphans++; return }
      const key = r.suppliers.id
      const ov = overrides[r.id] ?? { qty: r.approved_qty, selected: true }
      const selectable: SelectableRow = { ...r, qty: ov.qty, selected: ov.selected }
      if (!map.has(key)) map.set(key, { supplier: r.suppliers, rows: [] })
      map.get(key)!.rows.push(selectable)
    })
    return { groups: Array.from(map.values()), orphanCount: orphans }
  }, [rows, overrides])

  function setQty(id: string, qty: number) {
    setOverrides(prev => ({ ...prev, [id]: { qty: Math.max(0, qty), selected: prev[id]?.selected ?? true } }))
  }

  function toggle(id: string, current: boolean) {
    setOverrides(prev => ({ ...prev, [id]: { qty: prev[id]?.qty ?? 0, selected: !current } }))
  }

  function toggleAll(supplierRows: SelectableRow[], select: boolean) {
    const next = { ...overrides }
    supplierRows.forEach(r => {
      next[r.id] = { qty: overrides[r.id]?.qty ?? r.purchase_suggestion, selected: select }
    })
    setOverrides(next)
  }

  async function emit(supplier: Supplier, supplierRows: SelectableRow[]) {
    setError(null)
    setEmitting(supplier.id)

    const { data: { user } } = await supabase.auth.getUser()
    if (!user) { setError('Sem sessão'); setEmitting(null); return }
    const { data: profile } = await supabase.from('profiles').select('company_id').eq('id', user.id).single()
    if (!profile) { setError('Perfil não encontrado'); setEmitting(null); return }
    const { data: company } = await supabase.from('companies').select('*').eq('id', profile.company_id).single()
    if (!company) { setError('Empresa não encontrada'); setEmitting(null); return }

    const selected = supplierRows.filter(r => r.selected && r.qty > 0)
    if (selected.length === 0) {
      setError('Selecione pelo menos um item com quantidade > 0')
      setEmitting(null)
      return
    }

    const toItemInput = (r: SelectableRow) => ({
      product_id: r.id,
      sku: r.sku,
      product_name: r.name,
      quantity: r.qty,
      unit_cost: r.cost,
    })

    const compraItems = selected.filter(r => r.kanban_status === 'Compra Aprovada').map(toItemInput)
    const consigItems = selected.filter(r => r.kanban_status === 'Consignado').map(toItemInput)

    const results: Array<{ order: { id: string; order_number: string } } | { error: string }> = []

    if (compraItems.length > 0) {
      results.push(await emitPurchaseOrder({
        companyId: profile.company_id,
        company: company as Company,
        supplier,
        items: compraItems,
        order_type: 'compra',
      }))
    }
    if (consigItems.length > 0) {
      results.push(await emitPurchaseOrder({
        companyId: profile.company_id,
        company: company as Company,
        supplier,
        items: consigItems,
        order_type: 'consignacao',
      }))
    }

    setEmitting(null)

    const failed = results.find(r => 'error' in r) as { error: string } | undefined
    if (failed) {
      setError(failed.error)
      notify('error', failed.error)
      return
    }

    const successOrders = results.filter((r): r is { order: { id: string; order_number: string } } => 'order' in r)
    if (successOrders.length === 1) {
      notify('success', `Pedido ${successOrders[0].order.order_number} emitido.`)
      navigate(`/historico-pedidos?highlight=${successOrders[0].order.id}`)
    } else if (successOrders.length === 2) {
      notify('success', `2 pedidos emitidos (${successOrders.map(s => s.order.order_number).join(' e ')}).`)
      navigate(`/historico-pedidos?highlight=${successOrders[successOrders.length - 1].order.id}`)
    }
  }

  const fmtMoney = (n: number) => n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

  if (isMobile) {
    return (
      <AppLayout title="Emissão de Pedidos">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : groups.length === 0 && orphanCount === 0 ? (
          <div className="text-center text-ink-faint py-16">
            <p className="text-2xl mb-2">📨</p>
            <p>Nenhum item aguardando emissão.</p>
            <p className="text-xs text-ink-faint mt-2 px-4">Use a aba <strong>Painel</strong> e digite a quantidade nos itens com status "Compra Aprovada".</p>
          </div>
        ) : (
          <div className="space-y-3">
            {error && <p className="text-danger text-sm bg-danger/10 border border-danger/30 rounded-xl px-3 py-2">{error}</p>}
            {orphanCount > 0 && (
              <button
                onClick={() => navigate('/fornecedor?tab=vincular')}
                className="w-full bg-warn/15 border border-warn/40 rounded-xl px-3 py-2.5 text-left text-[#9a5a28] text-xs touch-action-manip active:bg-warn/25 dark:bg-warn/20 dark:text-amber-300"
              >
                <strong>{orphanCount}</strong> {orphanCount === 1 ? 'item sem fornecedor' : 'itens sem fornecedor'} — toque para vincular →
              </button>
            )}
            <MobilePurchaseReport
              groups={groups}
              emitting={emitting}
              onToggle={toggle}
              onToggleAll={toggleAll}
              onSetQty={setQty}
              onEmit={emit}
            />
          </div>
        )}
      </AppLayout>
    )
  }

  return (
    <AppLayout title="Emissão de Pedidos">
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-6 h-6 border-2 border-base-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : groups.length === 0 && orphanCount === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-ink-faint gap-2">
          <p className="text-base">Nenhum item aguardando emissão.</p>
          <p className="text-sm text-ink-faint max-w-md text-center">
            Para liberar pedidos aqui, vá no <button onClick={() => navigate('/')} className="underline hover:text-ink-muted">Painel BX</button> e
            digite a quantidade na coluna <strong>Quantidade do Pedido</strong>.
            Itens com <span className="px-1.5 py-0.5 rounded bg-[#ED7D31]/30 text-[#FFD8B0] text-xs font-medium">Compra Aprovada</span> aparecem nesta página.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {error && <p className="text-danger text-sm bg-danger/10 border border-danger/30 rounded-lg px-3 py-2">{error}</p>}

          <div className="bg-surface border border-line rounded-lg px-4 py-3 text-sm text-ink-muted">
            Itens com status <span className="px-1.5 py-0.5 rounded bg-[#ED7D31]/30 text-[#FFD8B0] text-xs font-medium">Compra Aprovada</span> ou <span className="px-1.5 py-0.5 rounded bg-[#FFC000] text-gray-900 text-xs font-medium">Consignado</span> no Painel BX e ainda sem pedido emitido. A quantidade vem da coluna <strong>Quantidade do Pedido</strong> — você pode ajustar aqui antes de emitir. Consignados geram <strong>Pedido de Consignação</strong>; aprovados geram <strong>Pedido de Compra</strong>.
          </div>

          {orphanCount > 0 && (
            <div className="bg-warn/15 border border-warn/40 rounded-lg px-4 py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 dark:bg-warn/20">
              <p className="text-sm text-[#9a5a28] dark:text-amber-300">
                <strong>{orphanCount}</strong> {orphanCount === 1 ? 'item (aprovado ou consignado) está' : 'itens (aprovados ou consignados) estão'} sem fornecedor vinculado e não {orphanCount === 1 ? 'aparece' : 'aparecem'} abaixo.
              </p>
              <button
                onClick={() => navigate('/fornecedor?tab=vincular')}
                className="text-xs px-3 py-1.5 bg-warn/20 hover:bg-warn/30 border border-warn/50 rounded text-[#7a4a20] whitespace-nowrap shrink-0 dark:text-amber-300"
              >
                Vincular fornecedores
              </button>
            </div>
          )}

          {groups.map(({ supplier, rows: gRows }) => {
            const selectedRows = gRows.filter(r => r.selected && r.qty > 0)
            const total = selectedRows.reduce((sum, r) => sum + r.qty * r.cost, 0)
            const allSelected = gRows.every(r => r.selected)

            return (
              <div key={supplier.id} className="bg-surface border border-line rounded-xl overflow-hidden">
                <div className="px-4 sm:px-5 py-3 border-b border-line flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
                  <div>
                    <h3 className="text-ink font-semibold">{supplier.name}</h3>
                    <p className="text-xs text-ink-faint mt-0.5">
                      {selectedRows.length} de {gRows.length} itens · Lead time {supplier.lead_time_days} dias
                    </p>
                  </div>
                  <div className="text-left sm:text-right">
                    <p className="text-xs text-ink-faint">Total selecionado</p>
                    <p className="text-lg font-semibold text-ink tabular-nums">{fmtMoney(total)}</p>
                  </div>
                </div>

                <div className="overflow-x-auto">
                <table className="w-full text-sm table-fixed">
                  <colgroup>
                    <col className="w-10" />
                    <col className="w-36" />
                    <col />
                    <col className="w-24" />
                    <col className="w-24" />
                    <col className="w-32" />
                    <col className="w-28" />
                    <col className="w-32" />
                  </colgroup>
                  <thead className="bg-surface border-b border-line">
                    <tr>
                      <th className="px-3 py-2 text-center">
                        <input type="checkbox" checked={allSelected} onChange={() => toggleAll(gRows, !allSelected)} />
                      </th>
                      <th className="text-left text-xs font-semibold text-ink-muted uppercase px-3 py-2">SKU</th>
                      <th className="text-left text-xs font-semibold text-ink-muted uppercase px-3 py-2">Produto</th>
                      <th className="text-right text-xs font-semibold text-ink-muted uppercase px-3 py-2">Estoque</th>
                      <th className="text-right text-xs font-semibold text-ink-muted uppercase px-3 py-2">Sugestão</th>
                      <th className="text-right text-xs font-semibold text-ink-muted uppercase px-3 py-2">Quantidade</th>
                      <th className="text-right text-xs font-semibold text-ink-muted uppercase px-3 py-2">Custo Unit.</th>
                      <th className="text-right text-xs font-semibold text-ink-muted uppercase px-3 py-2">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gRows.map(r => (
                      <tr key={r.id} className="border-b border-line">
                        <td className="px-3 py-2 text-center">
                          <input type="checkbox" checked={r.selected} onChange={() => toggle(r.id, r.selected)} />
                        </td>
                        <td className="px-3 py-2 text-xs truncate" title={r.sku}>
                          <span className={`mr-1.5 px-1.5 py-0.5 rounded text-[9px] font-semibold align-middle ${
                            r.kanban_status === 'Consignado'
                              ? 'bg-[#FFC000] text-gray-900'
                              : 'bg-[#ED7D31]/30 text-[#FFD8B0]'
                          }`}>
                            {r.kanban_status === 'Consignado' ? 'CONSIG' : 'COMPRA'}
                          </span>
                          <span className="font-mono text-ink-muted">{r.sku}</span>
                        </td>
                        <td className="px-3 py-2 text-ink truncate" title={r.name}>{r.name}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-ink-muted">{r.current_stock}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-ink-faint">{r.purchase_suggestion}</td>
                        <td className="px-3 py-2 text-right">
                          <input
                            type="number"
                            min={0}
                            value={r.qty}
                            onChange={e => setQty(r.id, Number(e.target.value))}
                            className="w-20 bg-paper border border-line rounded px-2 py-1 text-ink text-sm text-right tabular-nums"
                          />
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-ink-muted">{fmtMoney(r.cost)}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium text-ink">{fmtMoney(r.qty * r.cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>

                <div className="px-4 sm:px-5 py-3 border-t border-line flex justify-end">
                  <button
                    onClick={() => emit(supplier, gRows)}
                    disabled={emitting === supplier.id || selectedRows.length === 0}
                    className="px-4 py-2 bg-base-600 hover:bg-base-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    {emitting === supplier.id ? 'Emitindo...' : `Emitir Pedido (${selectedRows.length} itens)`}
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </AppLayout>
  )
}
